import MarkdownIt from "markdown-it";
import { fillEmptyTableCells } from "./fillEmptyTableCells";

// markdown-it core 규칙(tiptapTaskLists)에서 사용하는 타입을 라이브러리에서 추론한다.
type CoreRule = Parameters<MarkdownIt["core"]["ruler"]["push"]>[1];
type StateCore = Parameters<CoreRule>[0];

// 태스크리스트 마커: "[ ] " / "[x] " / "[X] "
const TASK_MARKER = /^\[([ xX])\]\s/;

// markdown-it 인스턴스는 모듈 스코프에서 1회 생성해 재사용한다.
const md = new MarkdownIt({
  html: false, // 원시 HTML 차단 → XSS/injection 방지
  linkify: true, // 맨몸 URL 자동 링크화
  breaks: true, // 단일 줄바꿈을 <br>로 보존
});
// 토큰 단계에서 직접 Tiptap 구조를 부여 → 렌더 후 DOM 후처리 불필요
md.core.ruler.after("inline", "tiptap-task-lists", tiptapTaskLists);

/**
 * 마크다운 문자열을 Tiptap 스키마에 맞는 HTML로 변환한다.
 * - GFM 테이블은 markdown-it 코어가 처리(빈 셀은 fillEmptyTableCells로 보정)
 * - 태스크리스트는 tiptapTaskLists 코어 규칙이 data-type 구조로 직접 생성
 */
export function markdownToHtml(markdown: string): string {
  return fillEmptyTableCells(md.render(markdown));
}

/**
 * "[ ] " / "[x] " 마커가 붙은 리스트 항목을 Tiptap TaskList/TaskItem이 인식하는
 * `data-type="taskList"` / `data-type="taskItem"` + `data-checked` 구조로 변환한다.
 * 체크박스 <input>은 만들지 않고 마커 텍스트를 제거한다.
 *
 * markdown-it-task-lists 플러그인 + DOM 후처리를 대체한다(순수 토큰 변환이라 DOM 비의존).
 */
function tiptapTaskLists(state: StateCore): void {
  const tokens = state.tokens;
  const taskListOpens = new Set<number>();

  // 1) 마커로 시작하는 항목을 taskItem으로 표시하고, 렌더 텍스트에서 마커를 제거한다.
  //    열린 리스트 인덱스를 스택으로 추적해 부모 리스트를 O(1)에 찾는다.
  const listStack: number[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const type = tokens[i].type;
    if (type === "bullet_list_open" || type === "ordered_list_open") {
      listStack.push(i);
      continue;
    }
    if (type === "bullet_list_close" || type === "ordered_list_close") {
      listStack.pop();
      continue;
    }
    if (
      type !== "inline" ||
      tokens[i - 1]?.type !== "paragraph_open" ||
      tokens[i - 2]?.type !== "list_item_open"
    ) {
      continue;
    }

    const inline = tokens[i];
    const marker = TASK_MARKER.exec(inline.content);
    if (!marker) {
      continue;
    }

    const checked = marker[1] !== " ";
    inline.content = inline.content.slice(marker[0].length);
    const firstChild = inline.children?.[0];
    if (firstChild) {
      firstChild.content = firstChild.content.replace(TASK_MARKER, "");
    }

    const li = tokens[i - 2];
    li.attrSet("data-type", "taskItem");
    li.attrSet("data-checked", checked ? "true" : "false");

    // 부모 리스트(스택 최상단)가 bullet list일 때만 taskList로 승격한다.
    const parent = listStack[listStack.length - 1];
    if (parent !== undefined && tokens[parent].type === "bullet_list_open") {
      tokens[parent].attrSet("data-type", "taskList");
      taskListOpens.add(parent);
    }
  }

  if (taskListOpens.size === 0) {
    return;
  }

  // 2) taskList 안의 일반 항목도 taskItem으로 강제한다(스키마: taskList > taskItem+).
  const itemStack: number[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const type = tokens[i].type;
    if (type === "bullet_list_open" || type === "ordered_list_open") {
      itemStack.push(i);
    } else if (type === "bullet_list_close" || type === "ordered_list_close") {
      itemStack.pop();
    } else if (type === "list_item_open") {
      const parent = itemStack[itemStack.length - 1];
      if (parent !== undefined && taskListOpens.has(parent) && !tokens[i].attrGet("data-type")) {
        tokens[i].attrSet("data-type", "taskItem");
        tokens[i].attrSet("data-checked", "false");
      }
    }
  }
}
